import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Loader, SwapResolvedMessage } from '../../../components';
import { NFTSwapContainer } from '../../../components/SwapKiwi/NFTSwapContainer';
import {
  selectAppState,
  selectEnteredEthAmount,
  selectSelectedNFTs,
  selectSwapResolvedData,
  selectActiveSwap,
  selectAnswerSwapStep,
} from '../../../ducks/app/selectors';
import { ReactComponent as StatusIndicatorAccept } from '../../../assets/svg/icons/status-indicator-step-2.svg';
import { ReactComponent as SwapInitiated } from '../../../assets/svg/swapFlow/initiated.svg';
import { AnswerSwapStep, AppState } from '../../../ducks/app/slice';
import { useSwap } from '../../../hooks/apollo/useSwap';
import { useWeb3React } from '@web3-react/core';
import {
  clearSelectedNFTs,
  resetAppState,
  setActiveSwap,
  storeAnswerSwapStep,
  storeAppState,
  storeSwapResolvedData,
} from '../../../ducks/app/actions';
import { INFT } from '../../../types/nft';
import { SwapStatus } from '../../../types/enums';
import { useVerifiedNFTsList } from '../../../hooks/useVerifiedNFTsList';
import { getAreNFTsVerified, getFeePlusEthAmount, getIdsAndAddressesAndAmounts } from '../../../utils/nft';
import { BadgeSizes, SKBadge } from '../../../components/SwapKiwi/Badge';
import { IconComp } from '../../../components/SwapKiwi/IconComponent';
import { ReactComponent as Pencil } from '../../../assets/svg/icons/pencil.svg';
import { ReactComponent as Refresh } from '../../../assets/svg/icons/refresh-cw-02.svg';
import { ReactComponent as RightArrow } from '../../../assets/svg/icons/arrow-right.svg';
import { ReactComponent as LeftArrow } from '../../../assets/svg/icons/arrow-left.svg';
import { Routes } from '../../../constants/routes';
import { PageHeader } from '../../../components/SwapKiwi/PageHeader';
import { NoNFTsContainer } from '../../../components/SwapKiwi/NoNFTsContainer';
import { ButtonSizes, SKButton } from '../../../components/SwapKiwi/Button';
import { ReactComponent as StatusIndicator } from '../../../assets/svg/icons/status-indicator.svg';
import { ReactComponent as XClose } from '../../../assets/svg/icons/x-close-bold.svg';
import { useSwapContract } from '../../../hooks/contract/useContract';
import { ContractTransaction } from 'ethers';
import { addPendingTransaction } from '../../../ducks/transactions/actions';
import cls from 'classnames';
import { NoDataContainer } from '../../../components/SwapKiwi/NoDataContainer';
import { AlertColor, AlertComp } from '../../../components/SwapKiwi/Alert';
import { getIsAllApproved } from '../../../ducks/nft/selectors';
import { truncateAddress } from '../../../utils/truncateAddress';
import { SwapStepSvg } from '../../../components/SwapKiwi/SwapList/SwapStepSvg';

interface successfulTxnMessage {
  title?: string;
  description?: string;
  color: AlertColor;
}

export const ResolveSwap: FC = () => {
  const history = useHistory();
  const appState = useSelector(selectAppState);
  const selectedNFTs = useSelector(selectSelectedNFTs);
  const { type, message, annotations } = useSelector(selectSwapResolvedData);
  const enteredEthAmount = useSelector(selectEnteredEthAmount);
  const answerSwapStep = useSelector(selectAnswerSwapStep);
  const allNFTsApproved = useSelector(getIsAllApproved);
  const dispatch = useDispatch();
  const activeSwap = useSelector(selectActiveSwap);
  const { id } = useParams<{ id: string }>();
  const { loading, data } = useSwap(id);
  const { account } = useWeb3React();
  const { data: verifiedNFTAddresses } = useVerifiedNFTsList();
  const activeSwapContract = useSwapContract(activeSwap?.contract);

  const [swapProcessLoading, setSwapProcessLoading] = useState(false);
  const [errorHasOccurred, setErrorHasOccurred] = useState(false);
  const [transactionSucceeded, setTransactionSucceeded] = useState(false);
  const [successfullAlertData, setSuccessfullAlertData] = useState<successfulTxnMessage>({
    title: '',
    description: '',
    color: AlertColor.KIWI,
  });

  useEffect(() => {
    if (data?.status === SwapStatus.PROPOSED) {
      dispatch(storeAppState(AppState.ANSWER_SWAP));
    }

    if (data?.status === SwapStatus.INITIATED && account?.toUpperCase() === data.fromUser.toUpperCase()) {
      dispatch(storeAppState(AppState.ANSWER_SWAP));
      dispatch(storeAnswerSwapStep(AnswerSwapStep.FINAL_SWAP_CONFIRMATION));
    }
    const isResolved =
      data?.status === SwapStatus.EXECUTED ||
      data?.status === SwapStatus.CANCELLED ||
      data?.status === SwapStatus.REJECTED;
    if (isResolved && message) dispatch(storeAppState(AppState.SWAP_RESOLVED));

    // store swap to appState
    if (data) {
      // TODO: this is only ran once, and doesn't contain the latest version of the swap data
      dispatch(setActiveSwap(data));
    }
  }, [data?.userNFTs.length, data?.status]);
  const onSelectNFTButton = (): void => {
    dispatch(storeAnswerSwapStep(AnswerSwapStep.PICK_NFT));
    history.push(Routes.CREATE_SWAP);
  };

  const displayTxnErrorAlert = (): void => {
    setErrorHasOccurred(true);
    setTimeout((): void => {
      setErrorHasOccurred(false);
    }, 5000);
  };

  const displayTxnSuccessAlert = (message: successfulTxnMessage): void => {
    setSuccessfullAlertData(message);
    setTransactionSucceeded(true);
    setTimeout((): void => {
      setTransactionSucceeded(false);
    }, 5000);
  };

  const getUserNfts = useMemo((): INFT[] => {
    if (selectedNFTs.length) {
      return getAreNFTsVerified(selectedNFTs, verifiedNFTAddresses);
    } else {
      return data ? getAreNFTsVerified(data.userNFTs, verifiedNFTAddresses) : [];
    }
  }, [selectedNFTs, data?.userNFTs, verifiedNFTAddresses]);
  const getOfferedNfts = useMemo(
    (): INFT[] => (data ? getAreNFTsVerified(data.offeredNFTs, verifiedNFTAddresses) : []),
    [data?.offeredNFTs, verifiedNFTAddresses],
  );

  const totalUserNFTs = getUserNfts.reduce((previousValue, currentValue) => {
    const current = currentValue.amount && parseInt(currentValue.amount) > 0 ? parseInt(currentValue.amount) : 1;
    return previousValue + current;
  }, 0);

  const totalOfferedNFTs = getOfferedNfts.reduce((previousValue, currentValue) => {
    const current = currentValue.amount && parseInt(currentValue.amount) > 0 ? parseInt(currentValue.amount) : 1;
    return previousValue + current;
  }, 0);

  const getUserEthAmount = (): string | undefined => enteredEthAmount?.toString() ?? data?.userEth;

  const onSecondUserInitiate = async (): Promise<void> => {
    setSwapProcessLoading(true);
    if (activeSwapContract && account && activeSwap) {
      const { addresses, ids, amounts } = getIdsAndAddressesAndAmounts(selectedNFTs);
      try {
        const contractFee = await activeSwapContract.fee();
        let tx: ContractTransaction | null = null;

        tx = await activeSwapContract.initiateSwap(activeSwap.swapId, addresses, ids, amounts, {
          value: getFeePlusEthAmount(contractFee, enteredEthAmount),
        });

        if (tx === null) return;
        dispatch(
          addPendingTransaction({
            tx,
            summary: 'Swap response',
            completeMessage: 'Successfully responded to swap',
            errorMessage: 'Responding to swap failed',
          }),
        );
        // TODO: Display loading popup and remove this wait
        await tx.wait(1);
        dispatch(clearSelectedNFTs());
        dispatch(storeAppState(AppState.SWAP_RESOLVED));
        dispatch(
          storeSwapResolvedData({
            type: 'success',
            message: 'The swap has been initiated!',
            annotations: 'toOpenSwaps',
          }),
        );
        displayTxnSuccessAlert({
          title: 'Success!',
          description: 'The swap has been initiated!',
          color: AlertColor.KIWI,
        });
      } catch (e) {
        displayTxnErrorAlert();
      }
    }
    setSwapProcessLoading(false);
  };
  const onSwapCancel = async (): Promise<void> => {
    setSwapProcessLoading(true);
    if (activeSwapContract && activeSwap) {
      try {
        const tx = await activeSwapContract.cancelSwap(activeSwap.swapId);
        dispatch(
          addPendingTransaction({
            tx,
            summary: 'Swap cancellation',
            completeMessage: 'Successfully cancelled swap',
            errorMessage: 'Cancelling swap failed',
          }),
        );
        // TODO: Display loading popup and remove this wait
        await tx.wait(1);
        dispatch(storeAppState(AppState.SWAP_RESOLVED));
        dispatch(clearSelectedNFTs());
        dispatch(
          storeSwapResolvedData({
            type: 'rejected',
            message: 'The swap was cancelled!',
          }),
        );
        displayTxnSuccessAlert({
          title: 'Cancelled',
          description: 'The swap was cancelled!',
          color: AlertColor.GOLD,
        });
      } catch (e) {
        displayTxnErrorAlert();
      }
    }
    setSwapProcessLoading(false);
  };
  const onFinalSwapConfirmation = async (): Promise<void> => {
    setSwapProcessLoading(true);
    if (activeSwapContract && account && activeSwap) {
      try {
        const tx = await activeSwapContract.acceptSwap(activeSwap.swapId);
        dispatch(
          addPendingTransaction({
            tx,
            summary: 'Swap accepting',
            completeMessage: 'Successfully accepted suggested swap',
            errorMessage: 'Accepting swap failed',
          }),
        );
        // TODO: Display loading popup and remove this wait
        await tx.wait(1);
        dispatch(clearSelectedNFTs());
        dispatch(storeAppState(AppState.SWAP_RESOLVED));
        dispatch(
          storeSwapResolvedData({
            type: 'success',
            message: 'The swap was successful!',
            annotations: 'toYourNFTs',
          }),
        );
        displayTxnSuccessAlert({ title: 'Success!', description: 'The swap was successful!', color: AlertColor.KIWI });
      } catch (e) {
        displayTxnErrorAlert();
      }
    }
    setSwapProcessLoading(false);
  };

  if (appState === AppState.SWAP_RESOLVED) {
    return (
      <>
        {transactionSucceeded && (
          <AlertComp
            color={successfullAlertData.color}
            className="w-full"
            title={successfullAlertData.title}
            description={successfullAlertData.description}
          />
        )}
        <SwapResolvedMessage type={type} message={message} annotations={annotations} />
      </>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const isAuthorized = account?.toLowerCase() === data?.fromUser || account?.toLowerCase() === data?.toUser;
  const isUserProposer = data?.fromUser.toLowerCase() === account?.toLowerCase() ? true : false;
  const counterpartWallet = isUserProposer ? data?.toUser.toLowerCase() : data?.fromUser.toLowerCase();

  const swapStatusUI: {
    [key: string]: {
      text: string;
      notification?: string;
    };
  } = {
    INITIATED: {
      text: 'Answered',
      notification: 'Accept or reject a swap',
    },
    PROPOSED: {
      text: 'Proposed',
      notification: 'You have a new swap',
    },
    EXECUTED: {
      text: 'Accepted',
    },
    REJECTED: {
      text: 'Rejected',
    },
    CANCELLED: {
      text: 'Cancelled',
    },
  };

  if ((!loading && !data) || !isAuthorized) {
    return (
      <NoDataContainer
        color="onion"
        message1="Oops, swap not found."
        hasCta
        description="An error occured or the page doesn't exist."
      />
    );
  }
  const userEthAmount = getUserEthAmount();
  const canEditSwap = data?.status === SwapStatus.PROPOSED && !isUserProposer;
  return (
    <section className="bg-onion-50">
      <PageHeader
        background="onion"
        notTopBorder={false}
        title="Resolve Swap"
        description={
          data?.status === SwapStatus.PROPOSED &&
          answerSwapStep !== AnswerSwapStep.SWAP_REVIEW &&
          getUserNfts.length > 0 &&
          data?.offeredNFTs.length === 0
            ? `<p class="text-lg text-cloud-900">You created a swap offer for <strong>${truncateAddress(
                counterpartWallet as string,
              )}</strong>. Now it's their turn. <br> They have to respond to your offer now to make it work.</p>`
            : data?.status === SwapStatus.PROPOSED &&
              answerSwapStep !== AnswerSwapStep.SWAP_REVIEW &&
              getUserNfts.length === 0 &&
              data?.offeredNFTs.length > 0
            ? `<p class="text-lg text-cloud-900"><strong>${truncateAddress(
                counterpartWallet as string,
              )}</strong> added their NFTs, now it’s your turn to respond to that. Add the NFTs you agreed on, approve them and accept the swap.</p>`
            : answerSwapStep === AnswerSwapStep.SWAP_REVIEW
            ? `<p class="text-lg text-cloud-900">Now that you have selected your NFTs, its time to accept the swap. <br> Please double-check the assets that you added and <strong>${truncateAddress(
                counterpartWallet as string,
              )}</strong> assets. Click the "Accept swap" button on the right to proceed.</p>`
            : answerSwapStep === AnswerSwapStep.FINAL_SWAP_CONFIRMATION && data?.isAnswerable
            ? `<p class="text-lg text-cloud-900">This is the last step. Check if <strong>${truncateAddress(
                counterpartWallet as string,
              )}</strong> added all the assets you agreed on. <br/> You can now either confirm or cancel the swap, but you can’t change anything anymore. </p>`
            : `<p class="text-lg text-cloud-900">Almost there. Now <strong>${truncateAddress(
                counterpartWallet as string,
              )}</strong> needs to confirm the swap. <br/>No worries, they can't change anything anymore in regards to swap content. They can only accept or reject the swap.</p>`
        }
        link={{ label: 'Learn More', url: '/faq' }}
        breadcrumbs={{
          links: [
            {
              label: 'Home',
              slug: Routes.LANDING_ROUTE,
            },
            {
              label: 'Open Swaps',
              slug: Routes.OPEN_SWAPS,
            },
            {
              label: 'Resolve Swap',
              slug: Routes.RESOLVE_SWAP,
            },
          ],
        }}
      />
      <div className="bg-onion-100 py-3 border-b border-cloud-600">
        <div className="app-container mx-auto">
          <div className="flex bg-white border border-cloud-900 rounded-xl py-2 px-1.75">
            {isUserProposer && data?.status === 'PROPOSED' && (
              <>
                <SwapStepSvg isUserProposer={isUserProposer} status={data?.status as SwapStatus} />
                <div className="ml-2">
                  {data?.fromUser && (
                    <p className="text-sm font-semibold text-cloud-900">
                      {isUserProposer ? 'Swap proposed by you' : `Swap proposed by ${truncateAddress(data?.fromUser)}`}
                    </p>
                  )}
                  <p className="text-base xs-only:text-sm text-cloud-900 flex items-center gap-x-0.8125 mt-0.5">
                    {swapStatusUI[data?.status as SwapStatus].text}
                  </p>
                  <p className="text-sm text-cloud-400 mt-0.5">
                    {data?.toUser && (
                      <span>
                        {isUserProposer ? (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting response from{' '}
                            <span className="font-semibold text-cloud-400">{truncateAddress(data?.toUser)}</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-x-0.5">
                            <span className="font-semibold text-cloud-400">{truncateAddress(data?.toUser)}</span>
                            is awating your response
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
              </>
            )}
            {!isUserProposer && data?.status === 'INITIATED' && (
              <>
                <SwapStepSvg isUserProposer={isUserProposer} status={data?.status as SwapStatus} />
                <div className="ml-2">
                  {data?.fromUser && (
                    <p className="text-sm font-semibold text-cloud-900">
                      {isUserProposer ? 'Swap proposed by you' : `Swap proposed by ${truncateAddress(data?.fromUser)}`}
                    </p>
                  )}
                  <p className="text-base xs-only:text-sm text-cloud-900 flex items-center gap-x-0.8125 mt-0.5">
                    {swapStatusUI[data?.status as SwapStatus].text}
                  </p>
                  <p className="text-sm text-cloud-400 mt-0.5">
                    {data?.toUser && (
                      <span>
                        {isUserProposer ? (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting for <span className="font-semibold text-cloud-400"> your </span>confirmation
                          </span>
                        ) : (
                          <span className="flex items-center gap-x-0.5">
                            Awaiting for
                            <span className="font-semibold text-cloud-400">{truncateAddress(data?.fromUser)}</span>
                            confirmation
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
              </>
            )}
            {data?.status === SwapStatus.PROPOSED &&
              answerSwapStep !== AnswerSwapStep.SWAP_REVIEW &&
              getUserNfts.length === 0 &&
              data?.offeredNFTs.length > 0 && (
                <>
                  <IconComp size={60} icon={StatusIndicator} />
                  <div className="pl-2 flex flex-col">
                    <p className="text-sm font-bold">Step 1 of 2</p>
                    <p className="text-md">Answering swap</p>
                    <p className="text-sm text-cloud-400">Select the NFTs you want to swap.</p>
                  </div>
                </>
              )}
            {answerSwapStep === AnswerSwapStep.SWAP_REVIEW && (
              <>
                <IconComp size={60} icon={StatusIndicatorAccept} />
                <div className="pl-2 flex flex-col">
                  <p className="text-sm font-bold">Step 2 of 2</p>
                  <p className="text-md">Accepting offered swap</p>
                  <p className="text-sm text-cloud-400">Accept the swap.</p>
                </div>
              </>
            )}
            {answerSwapStep === AnswerSwapStep.FINAL_SWAP_CONFIRMATION && (
              <>
                <IconComp
                  className={cls('fill-current', {
                    'text-blueberry-400': isUserProposer,
                    'text-gold-300': !isUserProposer,
                  })}
                  size={60}
                  icon={SwapInitiated}
                />
                <div className="pl-2 flex flex-col">
                  <p className="text-sm font-bold">Final step</p>
                  <p className="text-md">
                    {data?.isAnswerable
                      ? 'Waiting for you to confirm the swap.'
                      : `Waiting for ${counterpartWallet} to confirm the swap.`}
                  </p>
                  <p className="text-sm text-cloud-400">
                    {data?.isAnswerable
                      ? 'Please, press "Confirm swap" to complete the swap.'
                      : `Please, wait for ${counterpartWallet} to confirm the swap.`}
                  </p>
                </div>
              </>
            )}
            <div className="flex items-center justify-center gap-x-1.5 ml-auto">
              <SKButton
                color="tertiary"
                size={ButtonSizes.LG}
                iconLeft={LeftArrow}
                onClick={(): void => {
                  dispatch(resetAppState());
                  history.push(Routes.OPEN_SWAPS);
                }}
                className="ml-auto"
              >
                Back
              </SKButton>
              <SKButton
                color="secondary"
                disabled={swapProcessLoading}
                size={ButtonSizes.LG}
                iconRight={XClose}
                onClick={onSwapCancel}
                className="ml-auto"
              >
                Cancel swap
              </SKButton>
              {data?.status === SwapStatus.PROPOSED &&
                answerSwapStep !== AnswerSwapStep.SWAP_REVIEW &&
                getUserNfts?.length === 0 && (
                  <SKButton color="primary" size={ButtonSizes.LG} iconRight={RightArrow} onClick={onSelectNFTButton}>
                    Add NFTs
                  </SKButton>
                )}
              {answerSwapStep === AnswerSwapStep.SWAP_REVIEW && (
                <SKButton
                  color="primary"
                  disabled={!allNFTsApproved || swapProcessLoading}
                  size={ButtonSizes.LG}
                  iconRight={RightArrow}
                  onClick={onSecondUserInitiate}
                >
                  Accept swap
                </SKButton>
              )}
              {answerSwapStep === AnswerSwapStep.FINAL_SWAP_CONFIRMATION && data?.isAnswerable && (
                <SKButton
                  color="primary"
                  disabled={swapProcessLoading}
                  size={ButtonSizes.LG}
                  iconRight={RightArrow}
                  onClick={onFinalSwapConfirmation}
                >
                  Confirm swap
                </SKButton>
              )}
            </div>
          </div>
        </div>
      </div>
      {!allNFTsApproved && (
        <AlertComp
          color={AlertColor.GOLD}
          className="border-b border-cloud-900"
          title="NFT approval required"
          description="We need to be able to transfer tokens on your behalf. You can always revoke approvals. Please click the 'Approve' button on the NFT, execute the transaction and wait until it turns green."
        />
      )}
      {errorHasOccurred && (
        <AlertComp
          color={AlertColor.STRAWBERRY}
          className="border-b border-cloud-900"
          title="Transaction error"
          description="Seems like you rejected the transaction or an error has occurred. Please try again."
        />
      )}
      <div className="flex flex-wrap justify-between items-start app-container mx-auto py-4">
        <div className="user-nft-container w-46%">
          <div className="flex gap-1 items-center min-h-4 pb-2 mb-4 border-b border-cloud-900">
            <span className="text-display-sm mr-2">Your Offer</span>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {userEthAmount ? userEthAmount : 0}
              <span>ETH</span>
            </SKBadge>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {totalUserNFTs}
              <span>NFTs</span>
            </SKBadge>
            {getUserNfts && getUserNfts.length > 0 && canEditSwap && (
              <SKButton
                onClick={onSelectNFTButton}
                className="ml-auto"
                color="tertiary"
                size={ButtonSizes.LG}
                iconLeft={Pencil}
              >
                Edit
              </SKButton>
            )}
          </div>
          {totalUserNFTs > 0 && (
            <div className="flex flex-col justify-center items-center gap-y-1">
              <NFTSwapContainer NFTs={getUserNfts} convertToGridOnXL isTransferring />
            </div>
          )}
          {totalUserNFTs === 0 && (
            <NoNFTsContainer className="mx-auto w-20 h-21.5" innerContentClass="px-5 mb-10">
              <div className="flex flex-col items-center justify-center">
                <p className="mb-2 leading-6">Waiting for you to select your NFTs.</p>
                <SKButton
                  onClick={onSelectNFTButton}
                  className="mt-auto"
                  color="primary"
                  size={ButtonSizes.XL}
                  iconRight={RightArrow}
                >
                  <p className="text-base text-cloud-900">Add NFTs</p>
                </SKButton>
              </div>
            </NoNFTsContainer>
          )}
        </div>
        <div className="bg-cloud-900 w-4 h-4 mt-5 rounded-full flex items-center justify-center">
          <IconComp
            icon={Refresh}
            size={40}
            color="white"
            className={cls('stroke-current text-onion-50', {
              'animate-spin': swapProcessLoading,
            })}
          />
        </div>
        <div className="user-nft-container w-46%">
          <>
            <div className="flex gap-1 items-center min-h-4 pb-2 mb-4 border-b border-cloud-900">
              <span className="text-display-sm mr-2">Offered NFTs</span>
              <SKBadge size={BadgeSizes.LG} color="outline">
                {data?.offeredEth ? data?.offeredEth : 0}
                <span>ETH</span>
              </SKBadge>
              <SKBadge size={BadgeSizes.LG} color="outline">
                {totalOfferedNFTs}
                <span>NFTs</span>
              </SKBadge>
            </div>
            {totalOfferedNFTs > 0 && <NFTSwapContainer NFTs={getOfferedNfts} convertToGridOnXL />}
            {totalOfferedNFTs === 0 && (
              <NoNFTsContainer className="w-20 mx-auto h-21.5" innerContentClass="px-5">
                <p className="leading-6">
                  Waiting for {truncateAddress(counterpartWallet as string)} to select the NFTs.
                </p>
              </NoNFTsContainer>
            )}
          </>
        </div>
      </div>
    </section>
  );
};
